import React from 'react';
import { Helmet } from 'react-helmet-async';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  keywords?: string;
  schema?: any; // Allow custom schema
  lang?: string;
  noindex?: boolean;
  breadcrumbs?: BreadcrumbItem[];
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  ogType = 'website',
  ogImage = 'https://funcirclegames.com/og-banner.png', // Fallback to a branded path
  keywords,
  schema,
  lang = 'en',
  noindex = false,
  breadcrumbs
}) => {
  const siteTitle = 'Fun Circle Games';
  const defaultTitle = 'Fun Circle Games – Physics Video & Ball Simulation Generator';
  const fullTitle = title ? `${title} | ${siteTitle}` : defaultTitle;

  const fullDescription = description || "Fun Circle Games is a physics video generator for creating satisfying ball simulations, ASMR animations, and viral faceless content for TikTok and YouTube Shorts.";

  const siteUrl = 'https://funcirclegames.com';
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;

  const defaultKeywords = "Fun Circle Games, FunCircleGames, Fun Circle Games Creator Studio, ball simulation, physics simulator, ASMR physics videos, satisfying ball simulation, physics video generator, faceless video generator, TikTok shorts creator, YouTube shorts generator";
  const finalKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;

  // Default Software Schema
  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Fun Circle Games Creator Studio",
    "operatingSystem": "Web",
    "applicationCategory": "MultimediaApplication",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  // Default Organization Schema
  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Fun Circle Games",
    "url": siteUrl,
    "logo": `${siteUrl}/logo.png`,
    "sameAs": [
      "https://youtube.com/@funcirclegames",
      "https://instagram.com/funcirclegames",
      "https://discord.gg/cdwNFdbx",
      "https://snapchat.com/add/funcirclegames"
    ]
  };

  // WebSite Schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Fun Circle Games",
    "url": siteUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${siteUrl}/faq?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  // Breadcrumb Schema
  const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "name": item.name,
      "item": item.url.startsWith('http') ? item.url : `${siteUrl}${item.url}`
    }))
  } : null;

  const isProdHost = typeof window !== 'undefined' && 
    (window.location.hostname === 'funcirclegames.com' || window.location.hostname === 'www.funcirclegames.com');
  
  const robotsDirective = noindex
    ? "noindex, nofollow"
    : (isProdHost
      ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      : "noindex, nofollow, noarchive");

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <meta name="keywords" content={finalKeywords} />
      <link rel="canonical" href={fullCanonical} />

      {/* Brand Identity / Favicons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Fun Circle Games" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={fullCanonical} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@funcirclegames" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(softwareSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(orgSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}

      {/* AI Crawler Hints */}
      <meta name="robots" content={robotsDirective} />
    </Helmet>
  );
};

export default SEO;
